import React from 'react';
import { useLocation } from 'react-router-dom';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';

import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  useIngestionsQuery,
  useIngestionUpdatesSubscription,
} from '../../../types/generated-types';

import { IngestionsTable } from './ingestions-table';
import { IngestionUpload } from './ingestion-upload';

import {
  BottomMenuItems,
  useInjectableComponents,
} from '../../system/services/injectableComponentsManager';

import { updateCacheFromSubscriptionEvent } from '../../../helpers/subscriptionUtils';
import { useIsSmallScreen } from '../../system/SystemMediaQueriesProvider';
import { DialogContent } from '@mui/material';

export function Ingestions() {
  const isSmall = useIsSmallScreen();
  const { pathname } = useLocation();
  const { setContextMenuItems } = useInjectableComponents();

  /* State */
  const [open, setOpen] = React.useState(false);

  /* Queries, Subscriptions */
  const { loading, error, data } = useIngestionsQuery({
    fetchPolicy: 'cache-first',
  });
  // Subscribe to all ingestion inserts, updates, and deletes, and update the cache accordingly
  useIngestionUpdatesSubscription({
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
    onError: (error) => console.error(error),
  });

  /* Effects: Manage Bottom Nav Menu */
  React.useEffect(() => {
    const bottomMenu: BottomMenuItems = [
      {
        items: [
          {
            id: 'menu-item-add-ingestion',
            label: 'Upload Ingestion',
            icon: <UploadFileIcon fontSize="small" />,
            action: () => handleClickOpen(),
            permit: {
              action: 'create',
              subject: 'Ingestion',
            },
          },
        ],
      },
    ];
    setContextMenuItems(bottomMenu);
    return () => {
      setContextMenuItems(undefined);
    };
  }, [pathname, setContextMenuItems]);

  /* Util */
  const handleClickOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  return (
    <div
      style={{
        background: 'inherit',
        margin: isSmall ? '0px 4px' : '20px',
        display: 'flex',
        flexDirection: 'row',
        flex: 1,
        alignItems: 'stretch',
      }}
    >
      {error && (
        <Typography color="error" sx={{ textAlign: 'center', marginBottom: 2 }}>
          Error loading ingested properties
        </Typography>
      )}
      <IngestionsTable ingestions={data?.ingestions || []} loading={loading} />
      <IngestionUploadDialog open={open} handleClose={() => handleClose()} />
    </div>
  );
}

interface IngestionUploadDialogProps {
  open: boolean;
  handleClose: () => void;
}

const IngestionUploadDialog = (props: IngestionUploadDialogProps) => {
  const { open, handleClose } = props;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      sx={{
        '& .MuiDialog-paper': {
          maxWidth: '900px',
          overflowY: 'visible',
          margin: '0 5%',
        },
      }}
    >
      <DialogContent
        sx={{
          backgroundColor: '#36AFA5',
          maxWidth: '900px',
          display: 'flex',
          flexDirection: 'row',
          gap: '4px',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px 6px',
          overflowY: 'visible',
        }}
      >
        <UploadFileIcon sx={{ fontSize: '1.75rem', color: 'white' }} />
        <DialogTitle
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          id="alert-dialog-title"
          variant="h4"
          sx={{
            fontWeight: 'bold',
            color: 'white',
            textAlign: 'center',
            padding: '0',
            width: 'auto',
            fontSize: '1.2rem',
          }}
        >
          Property Ingestion Uploader
        </DialogTitle>
      </DialogContent>
      <IngestionUpload cancelDialog={handleClose} />
    </Dialog>
  );
};
